import styled from "@emotion/styled";
import InputLabel from "@mui/material/InputLabel";
import Paper from "@mui/material/Paper";
import TextField from "@mui/material/TextField";
import Box from "@mui/material/Box";
import Layout from "components/structure/Layout";
import React, { useContext, useEffect, useState } from "react";
import { Button, CircularProgress, Grid } from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import KeyboardBackspaceIcon from "@mui/icons-material/KeyboardBackspace";
import lables from "utils/lables.json";
import { SnackbarContext } from "context/snackBarContext";
import ImportSubGrowerService from "services/ImportSubGrowerService";

const disabledFieldSx = {
  width: { xs: "100%", md: "100%" },
  "& .MuiOutlinedInput-root": {
    height: "50px",
    "& fieldset": {
      borderColor: "#102A63 !important",
      borderWidth: "1px",
    },
    "&:hover fieldset": {
      borderColor: "#102A63 !important",
      borderWidth: "1px",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#102A63",
      borderWidth: "1px",
    },
    "&.Mui-disabled": {
      backgroundColor: "#f5f5f7",
      "& input": {
        color: "#000000",
        WebkitTextFillColor: "#000000",
        opacity: 1,
        fontWeight: "600",
        fontSize: "16px",
      },
      "& fieldset": {
        borderColor: "transparent",
      },
    },
  },
};

const labelSx = {
  width: "12rem",
  whiteSpace: "normal",
  overflow: "visible",
  textOverflow: "unset",
  wordBreak: "break-word",
};

const ViewDetailsSubGrower = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { setOpenAlert, setSeverity, setMessage } = useContext(SnackbarContext);

  const [subGrowerData, setSubGrowerData] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (id) {
      setLoading(true);
      ImportSubGrowerService.FetchSubGrowerLogDetail(id)
        .then((res) => {
          setSubGrowerData(res?.data?.response || {});
        })
        .catch((error) => {
          setSubGrowerData({});
          setMessage(error?.response?.data?.message || error?.message || "Failed to fetch sub grower details");
          setOpenAlert(true);
          setSeverity("error");
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [id]);

  return (
    <Layout title={lables.pageTitle}>
      <div style={{ paddingBottom: "4rem" }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 2,
          }}
        >
          <Heading>Import Sub Grower</Heading>
          <Button
            sx={{
              borderRadius: 100,
              minWidth: 95,
              minHeight: 44,
              background: "#102A62",
            }}
            variant="contained"
            startIcon={<KeyboardBackspaceIcon />}
            onClick={() => navigate(-1)}
          >
            {lables.backButton}
          </Button>
        </Box>

        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
            <CircularProgress />
          </Box>
        )}

        {/* Section 1: Upload result */}
        <PaperContainer>
          <form autoComplete="off">
            <Grid container spacing={2}>
              <Grid
                item
                md={6}
                xs={12}
                sx={{ display: { md: "flex", sx: "block" }, alignItems: "center" }}
              >
                <InputLabel sx={labelSx} htmlFor="operation">
                  {lables.importFarmerViewDetails.operationLabel}
                </InputLabel>
                <Box>
                  <TextField
                    sx={disabledFieldSx}
                    value={subGrowerData?.operation ?? ""}
                    disabled
                  />
                </Box>
              </Grid>
              <Grid
                item
                md={6}
                xs={12}
                sx={{ display: { md: "flex", xs: "block" }, alignItems: "center" }}
              >
                <InputLabel sx={labelSx} htmlFor="status">
                  {lables.statusPlaceholder}
                </InputLabel>
                <Box>
                  <TextField
                    sx={disabledFieldSx}
                    value={subGrowerData?.["import-status"] ?? ""}
                    disabled
                  />
                </Box>
              </Grid>
              <Grid
                item
                md={12}
                xs={12}
                sx={{ display: { md: "flex" }, alignItems: "center" }}
              >
                <InputLabel
                  sx={{ ...labelSx, alignSelf: "flex-start" }}
                  htmlFor="errorDescription"
                >
                  {lables.importFarmerViewDetails.errorDesc}
                </InputLabel>
                <Box sx={{ width: { xs: "100%" } }}>
                  <TextField
                    sx={{
                      ...disabledFieldSx,
                      "& .MuiOutlinedInput-root": {
                        ...disabledFieldSx["& .MuiOutlinedInput-root"],
                        height: "auto",
                      },
                    }}
                    value={subGrowerData?.["error-description"] ?? ""}
                    disabled
                    multiline
                    rows={4}
                  />
                </Box>
              </Grid>
            </Grid>
          </form>
        </PaperContainer>

        {/* Section 2: Sub Grower record fields */}
        <Box sx={{ mt: 1 }}>
          <PaperContainer>
            <form autoComplete="off">
              <Grid container spacing={2}>
                <Grid
                  item
                  md={4}
                  xs={12}
                  sx={{ display: { md: "flex", sx: "block" }, alignItems: "center" }}
                >
                  <InputLabel sx={labelSx} htmlFor="subGrowerCode">
                    Sub Grower Code
                  </InputLabel>
                  <Box>
                    <TextField
                      sx={disabledFieldSx}
                      value={subGrowerData?.["permanent-code"] ?? ""}
                      disabled
                    />
                  </Box>
                </Grid>
                <Grid
                  item
                  md={4}
                  xs={12}
                  sx={{ display: { md: "flex", xs: "block" }, alignItems: "center" }}
                >
                  <InputLabel sx={labelSx} htmlFor="subGrowerFullName">
                    Name
                  </InputLabel>
                  <Box>
                    <TextField
                      sx={disabledFieldSx}
                      value={subGrowerData?.["full-name"] ?? ""}
                      disabled
                    />
                  </Box>
                </Grid>
                <Grid
                  item
                  md={4}
                  xs={12}
                  sx={{ display: { md: "flex", sx: "block" }, alignItems: "center" }}
                >
                  <InputLabel sx={labelSx} htmlFor="subGrowerRegion">
                    Region
                  </InputLabel>
                  <Box>
                    <TextField
                      sx={disabledFieldSx}
                      value={subGrowerData?.region ?? ""}
                      disabled
                    />
                  </Box>
                </Grid>
                <Grid
                  item
                  md={4}
                  xs={12}
                  sx={{ display: { md: "flex", sx: "block" }, alignItems: "center" }}
                >
                  <InputLabel sx={labelSx} htmlFor="subGrowerLocation">
                    Location
                  </InputLabel>
                  <Box>
                    <TextField
                      sx={disabledFieldSx}
                      value={subGrowerData?.["geo-location"] ?? ""}
                      disabled
                    />
                  </Box>
                </Grid>
                <Grid
                  item
                  md={4}
                  xs={12}
                  sx={{ display: { md: "flex", xs: "block" }, alignItems: "center" }}
                >
                  <InputLabel sx={labelSx} htmlFor="phoneNumber">
                    Phone Number
                  </InputLabel>
                  <Box>
                    <TextField
                      sx={disabledFieldSx}
                      value={subGrowerData?.["phone-no"] ?? ""}
                      disabled
                    />
                  </Box>
                </Grid>
                <Grid
                  item
                  md={4}
                  xs={12}
                  sx={{ display: { md: "flex", xs: "block" }, alignItems: "center" }}
                >
                  <InputLabel sx={labelSx} htmlFor="propertyNumber">
                    Property Number
                  </InputLabel>
                  <Box>
                    <TextField
                      sx={disabledFieldSx}
                      value={subGrowerData?.["property-number"] ?? ""}
                      disabled
                    />
                  </Box>
                </Grid>
                <Grid
                  item
                  md={4}
                  xs={12}
                  sx={{ display: { md: "flex", xs: "block" }, alignItems: "center" }}
                >
                  <InputLabel sx={labelSx} htmlFor="subGrowerStatus">
                    Status
                  </InputLabel>
                  <Box>
                    <TextField
                      sx={disabledFieldSx}
                      value={subGrowerData?.status ?? ""}
                      disabled
                    />
                  </Box>
                </Grid>
                <Grid
                  item
                  md={4}
                  xs={12}
                  sx={{ display: { md: "flex", xs: "flex" }, alignItems: "center" }}
                >
                  <InputLabel sx={labelSx} htmlFor="cropYear">
                    Crop Year
                  </InputLabel>
                  <Box>
                    <TextField
                      sx={disabledFieldSx}
                      value={subGrowerData?.["crop-year"] ?? ""}
                      disabled
                    />
                  </Box>
                </Grid>
                <Grid
                  item
                  md={4}
                  xs={12}
                  sx={{ display: { md: "flex", xs: "flex" }, alignItems: "center" }}
                >
                  <InputLabel sx={labelSx} htmlFor="cropSeason">
                    Crop Season
                  </InputLabel>
                  <Box>
                    <TextField
                      sx={disabledFieldSx}
                      value={subGrowerData?.["crop-season"] ?? ""}
                      disabled
                    />
                  </Box>
                </Grid>
              </Grid>
            </form>
          </PaperContainer>
        </Box>
      </div>
    </Layout>
  );
};

const Heading = styled.div`
  color: rgba(115, 125, 134, 1);
  font-size: 1.25rem;
  font-weight: 600;
  margin-bottom: 1.5rem;
`;

const PaperContainer = styled(Paper)`
  padding: 2rem 1rem;
`;

export default ViewDetailsSubGrower;
